/** Docs (UA): see modules/game/DOCS.md — inline SVG icons (Espresso Brand art).
 *  Vector, not emoji: system emoji glyphs differ per Android build. */

import { GAME_PALETTE as P, rgbaFromHex } from '../../game.constants';

export interface GiftBoxIconProps {
    size?: number;
    opened?: boolean;
    /** Near-miss state: lid cracked open 6° with a seam of light — "it
     *  exists, you were close" (never shame, see the design doc 3.6). */
    ajar?: boolean;
}

export const GiftBoxIcon = ({ size = 30, opened = false, ajar = false }: GiftBoxIconProps) => (
    // overflow visible: the opened/ajar lid flies outside the 30x30 viewBox
    <svg width={size} height={size} viewBox="0 0 30 30" fill="none" overflow="visible" xmlns="http://www.w3.org/2000/svg">
        <rect x="4" y="12" width="22" height="15" rx="3" fill={P.gold} stroke={P.ink} strokeWidth="2.5" />
        {ajar && <rect x="5" y="11" width="20" height="2" rx="1" fill={P.goldGlow} />}
        <g transform={opened ? 'rotate(24 27 4) translate(6 -8)' : ajar ? 'rotate(-6 3 10) translate(0 -2.5)' : undefined}>
            <rect x="2.5" y="7" width="25" height="6.5" rx="2.5" fill={P.gold} stroke={P.ink} strokeWidth="2.5" />
            <path d="M15 7 C 11 1 5 2 7 6 Q 8.5 8 15 7 Z" fill={P.accent} stroke={P.ink} strokeWidth="2" />
            <path d="M15 7 C 19 1 25 2 23 6 Q 21.5 8 15 7 Z" fill={P.accent} stroke={P.ink} strokeWidth="2" />
        </g>
        <rect x="13" y={opened ? 14 : 7} width="4.5" height={opened ? 13 : 20} fill={P.accent} />
    </svg>
);

export interface HeartIconProps {
    size?: number;
    lost?: boolean;
    /** Lost hearts are cream-outlined for the dark canopy; on light surfaces
     *  (rule plates, ending cards) the outline must flip to ink or it vanishes. */
    onLight?: boolean;
    /** Rules plate: danger-red outline with a crack — "bug costs a life". */
    broken?: boolean;
}

export const HeartIcon = ({ size = 26, lost = false, onLight = false, broken = false }: HeartIconProps) => (
    <svg width={size} height={size} viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M13 22 C 5 16 2 11.5 2 8 A 5.5 5.5 0 0 1 13 6.5 A 5.5 5.5 0 0 1 24 8 C 24 11.5 21 16 13 22 Z"
            fill={lost || broken ? 'none' : P.danger}
            stroke={broken ? P.danger : lost ? (onLight ? rgbaFromHex(P.ink, 0.4) : rgbaFromHex(P.cream, 0.55)) : P.ink}
            strokeWidth={lost || broken ? 2.5 : 3}
            strokeLinejoin="round"
        />
        {broken && (
            <path d="M13 6.5 L10.5 12 L15 15 L12 21" stroke={P.danger} strokeWidth="2.2" fill="none" strokeLinejoin="round" />
        )}
        {!lost && !broken && <ellipse cx="8.5" cy="9" rx="3" ry="2" transform="rotate(-25 8.5 9)" fill={P.cream} opacity="0.6" />}
    </svg>
);

export const NoteIcon = ({ size = 26, muted = false }: { size?: number; muted?: boolean }) => (
    <svg width={size} height={size} viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M9.5 19 V7 L19.5 4.5 V16.5" stroke={P.uiText} strokeWidth="2.4" strokeLinejoin="round" fill="none" />
        <ellipse cx="7" cy="19" rx="3" ry="2.4" fill={P.uiText} />
        <ellipse cx="17" cy="16.5" rx="3" ry="2.4" fill={P.uiText} />
        {muted && <path d="M3 3 L23 23" stroke={P.danger} strokeWidth="2.6" strokeLinecap="round" />}
    </svg>
);
